import { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { X } from 'lucide-react';
import { Link } from 'react-router-dom';
import { useAuthContext } from '@/contexts/AuthContext';
import { useCategorias } from '@/hooks/useMarketplace';
import { useMarketplacePerfil } from '@/hooks/useMarketplacePerfil';
import { empresaVisivelNoMarketplace } from '@/lib/marketplace';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';

export function MarketplacePerfilCard() {
  const { t } = useTranslation();
  const { organization } = useAuthContext();
  const todasCategorias = useCategorias();
  const { categorias, cidades, guardarCategorias, adicionarCidade, removerCidade, guardarDescricao } = useMarketplacePerfil();

  const [sel, setSel] = useState<string[]>([]);
  const [descricao, setDescricao] = useState('');
  const [novaCidade, setNovaCidade] = useState('');

  useEffect(() => { if (categorias.data) setSel(categorias.data); }, [categorias.data]);
  useEffect(() => { setDescricao(organization?.descricao_publica ?? ''); }, [organization?.descricao_publica]);

  const visivel = empresaVisivelNoMarketplace(organization?.plano ?? null, organization?.subscription_status ?? null);
  const toggle = (id: string) => setSel((s) => s.includes(id) ? s.filter((x) => x !== id) : [...s, id]);

  return (
    <div className="ow-card">
      <div className="px-6 pt-5 pb-3">
        <h3 className="text-base font-bold tracking-tight">{t('marketplace.admin.title')}</h3>
        <p className="text-xs text-muted-foreground mt-0.5">{t('marketplace.admin.desc')}</p>
      </div>
      <div className="px-6 pb-5 flex flex-col gap-5">
        {!visivel && (
          <div className="text-xs rounded-md border border-border bg-muted/40 p-3">
            {t('marketplace.admin.gate')}{' '}
            <Link to="/configuracoes/plano" className="underline">{t('marketplace.admin.gateCta')}</Link>
          </div>
        )}

        <div>
          <label className="text-sm font-medium">{t('marketplace.admin.categories')}</label>
          <div className="flex flex-wrap gap-2 mt-2">
            {(todasCategorias.data ?? []).map((c) => (
              <button key={c.id} type="button" onClick={() => toggle(c.id)}
                className={`status-pill text-xs ${sel.includes(c.id) ? 'ring-2 ring-primary' : 'opacity-60'}`}>
                {c.nome}
              </button>
            ))}
          </div>
          <button type="button" className="ow-btn ow-btn--outline mt-3" disabled={guardarCategorias.isPending}
            onClick={() => guardarCategorias.mutate(sel)}>{t('marketplace.admin.saveCategories')}</button>
        </div>

        <div>
          <label className="text-sm font-medium">{t('marketplace.admin.cities')}</label>
          <div className="flex flex-wrap gap-2 mt-2">
            {(cidades.data ?? []).map((c) => (
              <span key={c.id} className="status-pill text-xs flex items-center gap-1">
                {c.cidade}
                <button type="button" aria-label={t('marketplace.admin.removeCity')} disabled={removerCidade.isPending} onClick={() => removerCidade.mutate(c.id)}><X className="h-3 w-3" /></button>
              </span>
            ))}
          </div>
          <form className="flex gap-2 mt-3" onSubmit={(e) => { e.preventDefault(); if (novaCidade.trim()) { adicionarCidade.mutate(novaCidade.trim()); setNovaCidade(''); } }}>
            <Input className="flex-1" value={novaCidade} onChange={(e) => setNovaCidade(e.target.value)} placeholder={t('marketplace.admin.addCityPlaceholder')} aria-label={t('marketplace.admin.cities')} />
            <button type="submit" className="ow-btn ow-btn--outline" disabled={adicionarCidade.isPending}>{t('marketplace.admin.addCity')}</button>
          </form>
        </div>

        <div>
          <label htmlFor="mkt-descricao" className="text-sm font-medium">{t('marketplace.admin.description')}</label>
          <Textarea id="mkt-descricao" className="mt-2" rows={3} value={descricao} onChange={(e) => setDescricao(e.target.value)} />
          <button type="button" className="ow-btn ow-btn--outline mt-2" disabled={guardarDescricao.isPending}
            onClick={() => guardarDescricao.mutate(descricao.trim())}>{t('marketplace.admin.saveDescription')}</button>
        </div>
      </div>
    </div>
  );
}
